import { bsMath, uiAmountToLamports } from "@bridgesplit/utils";
import {
    getWhirlpoolFromClmm,
    LoopRouteType,
    BaseRouteStepExpanded,
    LoanCreationParams,
    WhirlpoolPositionExpanded,
    getTokenMintFromClmm
} from "@bridgesplit/abf-sdk";
import { sqrtPriceX64ToTickIndex } from "@orca-so/whirlpool-client-sdk";
import { isPrincipalTokenWind, LoopExpanded } from "@bridgesplit/abf-react";
import { BN } from "bn.js";

import { BaseWinder } from "./BaseWinder";
import { LoopWinder, LoopWinderCtx } from "./types";

export class OrcaWinder extends BaseWinder implements LoopWinder {
    public readonly name = "OrcaWinder";

    getCollateralInputAmountForQuote(ctx: LoopWinderCtx): number | undefined {
        return 1;
    }

    getRouteOverrideParams(ctx: LoopWinderCtx): Partial<BaseRouteStepExpanded> | undefined {
        const { loopExpanded, form, whirlpoolPositions } = ctx;

        if (loopExpanded?.loopVault.routeType === LoopRouteType.Orca && form?.inputToken) {
            const whirlpoolPosition =
                getWhirlpoolFromClmm(form.clmmPosition) ?? getMatchingOrcaPosition(whirlpoolPositions, loopExpanded);

            if (whirlpoolPosition) {
                const whirlpoolPositionUsd = whirlpoolPosition.totalPrice;

                // for ocra positions that have value, pandora subtracts this total value from the principal input
                // for cases when getMatchingOrcaPosition is used, the value is 0, so the full principal input will be used for the currently empty position
                const positionPrincipalInput = uiAmountToLamports(
                    bsMath.div(whirlpoolPositionUsd, loopExpanded.principalOracle.usdPrice),
                    loopExpanded.principalToken.decimals
                );

                return {
                    positionParams: {
                        existingPosition: true,
                        principalMint: loopExpanded.principalToken.mint,
                        positionPrincipalInput
                    },
                    tickUpper: sqrtPriceX64ToTickIndex(new BN(whirlpoolPosition.position.upperSqrtPrice)),
                    tickLower: sqrtPriceX64ToTickIndex(new BN(whirlpoolPosition.position.lowerSqrtPrice))
                } as Partial<BaseRouteStepExpanded>;
            }

            if (isPrincipalTokenWind(loopExpanded.principalToken.mint, form.inputToken.mint)) {
                return {
                    positionParams: {
                        principalMint: loopExpanded.principalToken.mint,
                        positionPrincipalInput: uiAmountToLamports(
                            form.inputAmount,
                            loopExpanded.principalToken.decimals
                        )
                    }
                } as Partial<BaseRouteStepExpanded>;
            }
        }
        return undefined;
    }

    getLeveragedCollateralAmountToDeposit(ctx: LoopWinderCtx): number | undefined {
        const { loopExpanded, form, externalQuote } = ctx;
        if (!loopExpanded || !externalQuote || !form?.inputAmount || !form?.multiplier) return undefined;
        return 1;
    }

    getCollateralInputAmount(ctx: LoopWinderCtx): number | undefined {
        const { loopExpanded, inputAmount, clmmPosition, oraclePrices } = ctx;

        if (clmmPosition && loopExpanded) {
            const { principalToken } = loopExpanded;
            const principalOracle = oraclePrices?.get(principalToken.mint);
            const orcaPositionUsd = clmmPosition.totalPrice;

            const orcaPositionInPrincipal = bsMath.div(orcaPositionUsd, principalOracle?.usdPrice);
            return orcaPositionInPrincipal;
        }
        return inputAmount;
    }

    calculateMarketCollateralAmounts(ctx: LoopWinderCtx) {
        const { orcaMinOutputAmount } = ctx;
        const { totalOrcaMinOutputAmount, swappedOrcaMinOutputAmount } = orcaMinOutputAmount ?? {
            totalOrcaMinOutputAmount: 0,
            swappedOrcaMinOutputAmount: 0
        };

        return {
            totalMinOutputCollateralAmount: totalOrcaMinOutputAmount,
            swappedMinOutputCollateralAmount: swappedOrcaMinOutputAmount
        };
    }

    getLoanCreationParams(ctx: LoopWinderCtx): LoanCreationParams {
        const { loopExpanded, form, whirlpoolData, whirlpoolPositions } = ctx;

        if (loopExpanded?.loopVault.routeType === LoopRouteType.Orca && whirlpoolData?.whirlpoolOffchain) {
            const routeParams = loopExpanded.loopVault.quoteFetchParams as any;
            const offchain = whirlpoolData.whirlpoolOffchain;

            if (routeParams.type === "orca" && offchain?.tickSpacing && offchain?.tokenMintA && offchain?.tokenMintB) {
                const hasExistingPosition =
                    getWhirlpoolFromClmm(form?.clmmPosition) ??
                    getMatchingOrcaPosition(whirlpoolPositions, loopExpanded);

                if (!hasExistingPosition) {
                    return {
                        OrcaPositionCreate: {
                            upperTick: routeParams.tickUpper,
                            lowerTick: routeParams.tickLower,
                            tickSpacing: offchain.tickSpacing,
                            whirlpool: whirlpoolData.whirlpoolAddress || "",
                            tokenMintA: offchain.tokenMintA,
                            tokenMintB: offchain.tokenMintB
                        }
                    };
                }
            }
        }
        return "Default";
    }

    getPriceOverride(ctx: LoopWinderCtx): number | undefined {
        const { loopExpanded, inputAmount, orcaPosition } = ctx;
        if (orcaPosition) {
            return orcaPosition.totalPrice;
        } else {
            return loopExpanded?.principalOracle.getUsdAmount(inputAmount);
        }
    }

    getWindQuoteMint(ctx: LoopWinderCtx): string | undefined {
        const { form, whirlpoolPositions, loopExpanded } = ctx;

        const formPosition = getWhirlpoolFromClmm(form?.clmmPosition);
        const formNftMint = getTokenMintFromClmm(formPosition);

        const matchingOrcaPosition = getMatchingOrcaPosition(whirlpoolPositions, loopExpanded);
        const matchingOrcaPositionNftMint = getOrcaPositionMint(matchingOrcaPosition);

        const principalWindMint = loopExpanded?.principalToken.mint;

        const depositMint = formNftMint ?? matchingOrcaPositionNftMint ?? principalWindMint;

        return depositMint;
    }

    getFormInputMint(ctx: LoopWinderCtx): string | undefined {
        const { form, whirlpoolPositions, loopExpanded } = ctx;
        const formPosition = getWhirlpoolFromClmm(form?.clmmPosition);
        const formNftMint = getTokenMintFromClmm(formPosition);

        const matchingOrcaPosition = getMatchingOrcaPosition(whirlpoolPositions, loopExpanded);
        const matchingOrcaPositionNftMint = getOrcaPositionMint(matchingOrcaPosition);

        return formNftMint ?? matchingOrcaPositionNftMint ?? form?.inputToken?.mint;
    }
}

function getMatchingOrcaPosition(
    whirlpoolPositions: WhirlpoolPositionExpanded[] | undefined,
    loopExpanded: LoopExpanded | undefined
): WhirlpoolPositionExpanded | undefined {
    if (!loopExpanded || !whirlpoolPositions) return undefined;

    const routeParams = loopExpanded.loopVault.quoteFetchParams as any;

    const emptyPositions = whirlpoolPositions.filter((p) => !p.totalPrice);

    return emptyPositions.find((position) => {
        const upperTick = sqrtPriceX64ToTickIndex(new BN(position.position.upperSqrtPrice));
        const lowerTick = sqrtPriceX64ToTickIndex(new BN(position.position.lowerSqrtPrice));

        return upperTick === routeParams.tickUpper && lowerTick === routeParams.tickLower;
    });
}

function getOrcaPositionMint(whirlpoolPosition: WhirlpoolPositionExpanded | undefined): string | undefined {
    if (!whirlpoolPosition) return undefined;
    return whirlpoolPosition.position.positionMint;
}
